import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoginButton from "./LoginButton";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BarChartIcon from "@mui/icons-material/BarChart";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { nameToSlug } from "./PhotoUpload";

const getProfile = async (id) => {
  const athleteProfile = await (
    await fetch(
      `https://plantbuilt.s3.us-west-2.amazonaws.com/athleteProfiles/${id}.json`,
      { cache: "no-cache" }
    )
  ).json();
  return athleteProfile;
};

const cardSx = {
  p: 2,
  display: "flex",
  alignItems: "center",
  gap: 2,
  textDecoration: "none",
  color: "inherit",
  transition: "background-color 0.15s",
  "&:hover": { backgroundColor: "action.hover", cursor: "pointer" },
};

const ActionCard = ({ icon, title, description, to }) => (
  <Link to={to} style={{ textDecoration: "none" }}>
    <Paper sx={cardSx}>
      <Box sx={{ color: "#709425", display: "flex" }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body1" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <ChevronRightIcon sx={{ color: "text.secondary" }} />
    </Paper>
  </Link>
);

const UserContent = () => {
  const { isAuthenticated, user } = useAuth0();
  const [userProfile, setUserProfile] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchId, setSearchId] = useState("");
  const [allAthletes, setAllAthletes] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertStatus, setAlertStatus] = useState("");
  const [alertText, setAlertText] = useState("");

  useEffect(() => {
    if (isAuthenticated && !Object.keys(userProfile).length) {
      (async () => {
        setIsLoading(true);
        try {
          if (!user.email) {
            console.log("could not access email");
            setIsLoading(false);
            return;
          }
          const response = await fetch(
            "https://7ogouyltt5.execute-api.us-west-2.amazonaws.com/default/findUser",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: user.email.toLowerCase(),
            }
          );
          const id = await response.json();

          if (!id) {
            setShowAlert(true);
            setAlertStatus("error");
            setAlertText(
              "There was a problem verifying your account. Please contact admin."
            );
            return;
          }
          const adminIds = [100, 101, 300, 301];
          if (adminIds.includes(id)) setIsAdmin(true);

          const profile = await getProfile(id);
          if (profile) setUserProfile(profile);
          else if (!adminIds.includes(id)) {
            setShowAlert(true);
            setAlertStatus("error");
            setAlertText(
              "Please contact admin, there was an issue retrieving your profile."
            );
          }
        } catch (e) {
          console.error(e);
          setShowAlert(true);
          setAlertStatus("error");
          setAlertText(
            `There was a problem verifying your account. Your email, ${user.email}, may not be registered. Please contact admin.`
          );
          setUserProfile({});
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [isAuthenticated, user, userProfile]);

  useEffect(() => {
    if (!isAdmin) return;
    fetch("https://plantbuilt.s3.us-west-2.amazonaws.com/athletes.json", {
      cache: "no-cache",
    })
      .then((r) => r.json())
      .then((data) =>
        setAllAthletes(data.sort((a, b) => a.name.localeCompare(b.name)))
      )
      .catch(console.error);
  }, [isAdmin]);

  const adminUpdate = useCallback(
    async (id) => {
      const profile = await getProfile(id ?? searchId);
      setUserProfile(profile);
    },
    [searchId]
  );

  const hasProfile = Object.keys(userProfile).length > 0;

  if (!isAuthenticated) {
    return (
      <Box sx={{ textAlign: "center", mt: 12, mb: 6, px: 2 }}>
        <Typography
          variant="h3"
          fontFamily="Impact"
          color="#709425"
          gutterBottom
        >
          Athlete Portal
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Log in to manage your profile
        </Typography>
        <LoginButton />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 520, mx: "auto", mt: 4, mb: 6, px: 2, width: "100%" }}>
      {showAlert && (
        <Snackbar
          open={showAlert}
          autoHideDuration={6000}
          onClose={() => setShowAlert(false)}
        >
          <Alert
            onClose={() => setShowAlert(false)}
            severity={alertStatus || "info"}
            variant="filled"
          >
            {alertText}
          </Alert>
        </Snackbar>
      )}

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontFamily="Impact">
          Welcome, {user.name}
        </Typography>
        <LoginButton />
      </Box>

      {/* Athlete actions */}
      {isLoading && (
        <CircularProgress size={28} sx={{ color: "#709425", mt: 1 }} />
      )}
      {!isLoading && hasProfile && (
        <Stack spacing={1.5}>
          <ActionCard
            icon={<EditIcon />}
            title="Update Profile"
            description="Edit your bio and sport details"
            to={{ pathname: "/athlete-portal/update-profile", state: { userProfile } }}
          />
          <ActionCard
            icon={<PhotoCameraIcon />}
            title="Upload Photo"
            description="Replace your profile photo"
            to={{ pathname: "/athlete-portal/upload-photo", state: { userProfile, isAdmin } }}
          />
          {userProfile.published !== false && (
            <ActionCard
              icon={<PersonIcon />}
              title="View My Profile"
              description="See your public athlete page"
              to={`/athletes/${nameToSlug(userProfile.name)}`}
            />
          )}
        </Stack>
      )}

      {/* Admin section */}
      {isAdmin && (
        <>
          <Divider sx={{ my: 3 }}>
            <Typography variant="overline" color="text.secondary">
              Admin
            </Typography>
          </Divider>

          <Paper sx={{ p: 2.5 }}>
            <Autocomplete
              options={allAthletes}
              getOptionLabel={(a) => `${a.name} (${a.id})`}
              onChange={(_, athlete) => {
                if (athlete) adminUpdate(athlete.id);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Search athlete by name" fullWidth />
              )}
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
              <TextField
                label="Or enter ID"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" onClick={() => adminUpdate()}>
                Search
              </Button>
            </Box>
            {userProfile?.name && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                Loaded: <strong>{userProfile.name}</strong>
              </Typography>
            )}

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={1}>
              <Link
                to={{
                  pathname: "/athlete-portal/add-athlete",
                  state: {
                    isAdmin: true,
                    suggestedId: allAthletes.length
                      ? Math.max(...allAthletes.map((a) => a.id)) + 1
                      : "",
                  },
                }}
                style={{ textDecoration: "none" }}
              >
                <Button
                  variant="outlined"
                  color="success"
                  fullWidth
                  startIcon={<PersonAddIcon />}
                >
                  Add New Athlete
                </Button>
              </Link>
              <Link
                to="/athlete-portal/update-stats"
                style={{ textDecoration: "none" }}
              >
                <Button
                  variant="outlined"
                  color="success"
                  fullWidth
                  startIcon={<BarChartIcon />}
                >
                  Update Team Stats
                </Button>
              </Link>
            </Stack>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default UserContent;
